/**
 * useIssueDeepLink.jsx — Iter 345 (Feb 2026)
 *
 * Shared React hook + inline highlight style for deep-linking into the
 * Issue Reports list from the Analyzer drill modal. When the URL carries
 * a `?ticket=BUG-####` query param, the hook:
 *
 *   1. Returns the target ticket_id string (or null).
 *   2. Provides a `registerRow(ticket_id, node)` ref-setter that consuming
 *      components call on each rendered row. Once the matching node is
 *      registered, the hook scrolls it into view and applies a temporary
 *      "pulse" outline via CSS class for ~2.5s.
 *   3. Fires exactly once per URL change (guarded by an internal ref) so
 *      re-fetches, refresh clicks, and status updates don't re-scroll.
 *
 * The <IssueDeepLinkStyles /> component injects the pulse keyframes once.
 * It is safe to mount from multiple places (guards duplicate <style>).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STYLE_ID = "issue-deep-link-pulse-style";
const PULSE_MS = 2500;

/**
 * Reads ?ticket= from window.location.search. Returns the raw string or "".
 */
function readTicketParam() {
  try {
    return new URLSearchParams(window.location.search).get("ticket") || "";
  } catch {
    return "";
  }
}

export function useIssueDeepLink() {
  const [ticketId, setTicketId] = useState(() => readTicketParam());
  const rowsRef = useRef(new Map()); // ticket_id -> DOM node
  const firedForRef = useRef(null);  // ticket_id we already scrolled to
  const pollHandleRef = useRef(null); // active setInterval id (if any)

  // Re-read the URL param whenever the browser navigates (popstate, replaceState).
  useEffect(() => {
    const sync = () => setTicketId(readTicketParam());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  // Performs the scroll + pulse. Idempotent: guarded by firedForRef so it
  // only ever runs once per ticket_id. Uses two rAF passes so we scroll
  // AFTER the row has (a) appeared in the DOM and (b) been expanded, which
  // pushes real content into the layout.
  const triggerScroll = useCallback((tid, node) => {
    if (!tid || !node) return;
    if (firedForRef.current === tid) return;
    firedForRef.current = tid;
    if (pollHandleRef.current) {
      clearInterval(pollHandleRef.current);
      pollHandleRef.current = null;
    }
    const runScroll = () => {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        node.scrollIntoView();
      }
      node.classList.add("issue-deep-link-pulse");
      node.setAttribute("data-testid-highlight", `deep-link-highlighted-${tid}`);
      setTimeout(() => {
        node.classList.remove("issue-deep-link-pulse");
        node.removeAttribute("data-testid-highlight");
      }, PULSE_MS);
    };
    // Double-rAF: wait for the current paint + next paint so any auto-
    // expand toggled by the parent component has committed and the row's
    // final layout height is stable before we scroll.
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(runScroll);
      });
    } else {
      setTimeout(runScroll, 32);
    }
  }, []);

  // registerRow is called by each rendered row via its `ref` callback.
  // If the newly-registered node matches the deep-link target, fire
  // immediately (reactive) — no polling required in the common case.
  const registerRow = useCallback((tid, node) => {
    if (!tid) return;
    if (node) {
      rowsRef.current.set(tid, node);
      if (ticketId && tid === ticketId && firedForRef.current !== ticketId) {
        triggerScroll(tid, node);
      }
    } else {
      rowsRef.current.delete(tid);
    }
  }, [ticketId, triggerScroll]);

  // Backup polling: covers edge cases where React commits the row before
  // the effect below runs (rare) OR the row registers before ticketId was
  // read from the URL. Polls for up to 15s with 150ms cadence.
  useEffect(() => {
    if (!ticketId || firedForRef.current === ticketId) return;
    // Immediate check in case the row is already registered.
    const existing = rowsRef.current.get(ticketId);
    if (existing) {
      triggerScroll(ticketId, existing);
      return;
    }
    let attempts = 0;
    const maxAttempts = 100; // 100 * 150ms = 15s (initial-boot friendly)
    if (pollHandleRef.current) clearInterval(pollHandleRef.current);
    pollHandleRef.current = setInterval(() => {
      attempts += 1;
      const node = rowsRef.current.get(ticketId);
      if (node) {
        triggerScroll(ticketId, node);
      } else if (attempts >= maxAttempts) {
        clearInterval(pollHandleRef.current);
        pollHandleRef.current = null;
      }
    }, 150);
    return () => {
      if (pollHandleRef.current) {
        clearInterval(pollHandleRef.current);
        pollHandleRef.current = null;
      }
    };
  }, [ticketId, triggerScroll]);

  // Expose a reset so consumers can retrigger after a manual reload.
  const clearFired = useCallback(() => {
    firedForRef.current = null;
  }, []);

  return { ticketId, registerRow, clearFired };
}

/**
 * Injects the pulse keyframes once per document. Safe to render multiple
 * times — the second mount is a no-op.
 */
export function IssueDeepLinkStyles() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      @keyframes issueDeepLinkPulse {
        0%   { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.55); outline-color: rgba(37, 99, 235, 0.75); }
        60%  { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0.00); outline-color: rgba(37, 99, 235, 0.75); }
        100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.00); outline-color: rgba(37, 99, 235, 0.00); }
      }
      .issue-deep-link-pulse {
        outline: 2px solid rgba(37, 99, 235, 0.75);
        outline-offset: 2px;
        animation: issueDeepLinkPulse 2.4s ease-out 1;
        border-radius: 10px;
        scroll-margin-top: 96px;
      }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
}
